<template>
  <DashboardLayout>
    <div class="d-flex align-items-center gap-2 mb-4 page-identifier">
      <router-link :to="{ name: 'ViewBudgets' }" class="mb-0 main-identifier fw-semibold text-decoration-none">
        Budgets
      </router-link>
      <span class="material-symbols-outlined">keyboard_arrow_right</span>
      <router-link v-if="budgetId" :to="{ name: 'ViewBudget', params: { id: budgetId } }"
        class="mb-0 main-identifier fw-semibold text-decoration-none">
        View Budget
      </router-link>
      <span v-if="budgetId" class="material-symbols-outlined">keyboard_arrow_right</span>
      <p class="mb-0 sub-identifier">View ELI Budget</p>
    </div>

    <div class="d-flex justify-content-center mb-5">
      <div class="form-outer">
        <div class="crud-form position-relative">

          <!-- White overlay while loading -->
          <div v-if="loadingDetails" class="loading-overlay">
            <div class="spinner-border text-primary" role="status" aria-hidden="true"></div>
            <p class="mt-3 mb-0 text-muted">Loading ELI budget...</p>
          </div>

          <div class="form-inner">
            <div class="form-header d-flex justify-content-between align-items-center">
              <h1 class="mb-0">ELI Budget</h1>
            </div>

            <div class="form-body">

              <div v-if="error && !loadingDetails" class="alert alert-warning mt-3" role="alert">
                {{ error }}
              </div>

              <div class="row mt-2 mb-3">

                <div class="col-md-12 row form-group position-relative floating-label-group">
                  <div class="col-md-3 label-left d-flex justify-content-end align-items-center">
                    <label class="form-label">MDA</label>
                  </div>
                  <div class="col-md-9 input-right">
                    <input type="text" class="form-control view-only-input"
                      :value="form.mdaName || 'N/A'" readonly />
                  </div>
                </div>

                <div class="col-md-12 row form-group position-relative floating-label-group">
                  <div class="col-md-3 label-left d-flex justify-content-end align-items-center">
                    <label class="form-label">Economic Line Item</label>
                  </div>
                  <div class="col-md-9 input-right">
                    <input type="text" class="form-control view-only-input"
                      :value="form.econLineItemName || 'N/A'" readonly />
                  </div>
                </div>

                <div class="col-md-12 row form-group position-relative floating-label-group">
                  <div class="col-md-3 label-left d-flex justify-content-end align-items-center">
                    <label class="form-label">Economic Code</label>
                  </div>
                  <div class="col-md-9 input-right">
                    <input type="text" class="form-control view-only-input"
                      :value="form.economicCode || 'N/A'" readonly />
                  </div>
                </div>

                <div class="col-md-12 row form-group position-relative floating-label-group">
                  <div class="col-md-3 label-left d-flex justify-content-end align-items-center">
                    <label class="form-label">Org Code</label>
                  </div>
                  <div class="col-md-9 input-right">
                    <input type="text" class="form-control view-only-input"
                      :value="form.orgCode || 'N/A'" readonly />
                  </div>
                </div>

                <div class="col-md-12 row form-group position-relative floating-label-group">
                  <div class="col-md-3 label-left d-flex justify-content-end align-items-center">
                    <label class="form-label">Year</label>
                  </div>
                  <div class="col-md-9 input-right">
                    <input type="text" class="form-control view-only-input"
                      :value="form.year || 'N/A'" readonly />
                  </div>
                </div>

                <div class="col-md-12 row form-group position-relative floating-label-group">
                  <div class="col-md-3 label-left d-flex justify-content-end align-items-center">
                    <label class="form-label">Amount Budgeted</label>
                  </div>
                  <div class="col-md-9 input-right">
                    <input type="text" class="form-control view-only-input"
                      :value="formatCurrency(form.amountBudgeted || 0)" readonly />
                  </div>
                </div>

                <div class="col-md-12 row form-group position-relative floating-label-group">
                  <div class="col-md-3 label-left d-flex justify-content-end align-items-center">
                    <label class="form-label">Revenue Amount</label>
                  </div>
                  <div class="col-md-9 input-right">
                    <input type="text" class="form-control view-only-input"
                      :value="formatCurrency(form.revenueAmount || 0)" readonly />
                  </div>
                </div>

                <div class="col-md-12 row form-group position-relative floating-label-group">
                  <div class="col-md-3 label-left d-flex justify-content-end align-items-center">
                    <label class="form-label">Amount Released</label>
                  </div>
                  <div class="col-md-9 input-right">
                    <input type="text" class="form-control view-only-input"
                      :value="formatCurrency(form.amountReleased || 0)" readonly />
                  </div>
                </div>

                <div class="col-md-12 row form-group position-relative floating-label-group">
                  <div class="col-md-3 label-left d-flex justify-content-end align-items-center">
                    <label class="form-label">Balance</label>
                  </div>
                  <div class="col-md-9 input-right">
                    <input type="text" class="form-control view-only-input"
                      :value="formatCurrency(form.balance || 0)" readonly />
                  </div>
                </div>

              </div>
            </div>
          </div>

          <div v-if="!loadingDetails && !error && hasRole('qssu')" class="mt-4">
            <div class="d-flex justify-content-center">
              <button type="button" class="btn btn-primary submit-btn" @click="goToEdit">
                Edit ELI Budget
              </button>
            </div>
          </div>

        </div>
      </div>
    </div>
  </DashboardLayout>
</template>

<script setup>
import { ref, reactive, onMounted } from 'vue'
import axios from 'axios'
import DashboardLayout from '../layout/DashboardLayout.vue'
import Cookies from 'js-cookie'
import { useRoute, useRouter } from 'vue-router'
import { useUserStore } from '@/stores/user'

const route = useRoute()
const router = useRouter()
const userStore = useUserStore()

const id = route.params.id
const budgetId = route.query.budgetId || null

const root_api_url = process.env.VUE_APP_API_ROOT_URL

const error = ref('')
const loadingDetails = ref(false)

const hasRole = (role) => !!(userStore.profile?.role || {})[role]

const form = reactive({
  mdaName: '',
  econLineItemName: '',
  economicCode: '',
  orgCode: '',
  year: '',
  amountBudgeted: 0,
  revenueAmount: 0,
  amountReleased: 0,
  balance: 0,
  mdaId: '',
  economicLineItemId: '',
  version: null,
  createdDate: '',
  lastModifiedDate: '',
  createdBy: '',
  lastModBy: '',
  userId: '',
  title: '',
  performance: 0,
  budgetDate: '',
  totalRecAmtBudgeted: 0,
  totalCapAmtBudgeted: 0,
  totalRecAmtReleased: 0,
  totalCapAmtReleased: 0,
})

const fetchEliBudget = async () => {
  if (!id) {
    error.value = 'No ELI budget ID provided.'
    return
  }
  error.value = ''
  loadingDetails.value = true
  const token = Cookies.get('auth_token')
  try {
    const { data } = await axios.get(`${root_api_url}/eli/mda-eli-budget/${id}`, {
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    })

    form.mdaName = data.mdaName || ''
    form.econLineItemName = data.econLineItemName || ''
    form.economicCode = data.economicCode || ''
    form.orgCode = data.orgCode || ''
    form.year = data.year || ''
    form.amountBudgeted = data.amountBudgeted || 0
    form.revenueAmount = data.revenueAmount || 0
    form.amountReleased = data.amountReleased || 0
    form.balance = data.balance || 0
    form.mdaId = data.mdaId || ''
    form.economicLineItemId = data.economicLineItemId || ''
    form.version = data.version ?? null
    form.createdDate = data.createdDate || ''
    form.lastModifiedDate = data.lastModifiedDate || ''
    form.createdBy = data.createdBy || ''
    form.lastModBy = data.lastModBy || ''
    form.userId = data.userId || ''
    form.title = data.title || ''
    form.performance = data.performance || 0
    form.budgetDate = data.budgetDate || ''
    form.totalRecAmtBudgeted = data.totalRecAmtBudgeted || 0
    form.totalCapAmtBudgeted = data.totalCapAmtBudgeted || 0
    form.totalRecAmtReleased = data.totalRecAmtReleased || 0
    form.totalCapAmtReleased = data.totalCapAmtReleased || 0
  } catch (err) {
    console.error('Failed to fetch ELI budget:', err)
    error.value = 'Failed to load ELI budget. Please try again.'
  } finally {
    loadingDetails.value = false
  }
}

const goToEdit = () => {
  router.push({
    name: 'EditEliBudget',
    params: { id },
    query: { budgetId: budgetId || undefined },
  })
}

const formatCurrency = (amount) => {
  return '₦' + Number(amount || 0).toLocaleString('en-NG', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })
}

onMounted(async () => {
  if (!userStore.profile) {
    await userStore.fetchProfile()
  }
  await fetchEliBudget()
  document.title = 'View ELI Budget'
})
</script>

<style scoped>
.loading-overlay {
  position: absolute;
  inset: 0;
  z-index: 20;
  background: rgba(255, 255, 255, 0.95);
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  border-radius: inherit;
}

.view-only-input {
  background-color: transparent;
  border: none;
  padding-left: 0;
  cursor: default;
  pointer-events: none;
  color: #212529;
  font-size: 1rem;
}

.submit-btn {
  background-color: #3498db !important;
  border-color: #3498db !important;
  min-width: 160px;
}

.submit-btn:hover {
  background-color: #2980b9 !important;
  border-color: #2980b9 !important;
}
</style>
